import { useState, useCallback, useEffect, useRef } from 'react';
import Editor from './components/Editor';
import AboutPanel from './components/AboutPanel';
import ShortcutsOverlay from './components/ShortcutsOverlay';
import { initCheerpJ, runZeta, isCheerpJReady } from './runtime/cheerpj';
import type { RunResult, ZetaError } from './runtime/types';
import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';
import './App.css';

const DEFAULT_CODE = `tell {Hello, World!}`;

const EXAMPLES: { name: string; code: string }[] = [
  {
    name: 'Hello, World',
    code: 'tell {Hello, World!}',
  },
  {
    name: 'Arithmetic',
    code: `global pi is 3.14
local radius is 5
local area is pi * radius ^ 2
tell {The area is } area`,
  },
  {
    name: 'Variables & Scoping',
    code: `global max is 100
local count is 0
tell {Max is } max
tell {Count is } count
count is now count + 1
tell {Count is now } count`,
  },
  {
    name: 'Operator Precedence',
    code: `local a is 2
local b is 3
local c is 4
local expr1 is a + b * c
local expr2 is (a + b) * c
local expr3 is a ^ b ^ 2
local expr4 is 10 / 3 + 10 % 3
tell {a + b * c = } expr1
tell {(a + b) * c = } expr2
tell {a ^ b ^ 2 = } expr3
tell {10 / 3 + 10 % 3 = } expr4`,
  },
  {
    name: 'Syntax Error',
    code: `tell {Hello
local x is 10`,
  },
];

function getCodeFromHash(): string | null {
  const hash = window.location.hash;
  if (hash.startsWith('#code=')) {
    try {
      return decompressFromEncodedURIComponent(hash.slice(6)) || null;
    } catch {
      return null;
    }
  }
  return null;
}

function setCodeHash(code: string) {
  const encoded = compressToEncodedURIComponent(code);
  window.location.hash = 'code=' + encoded;
}

type Theme = 'light' | 'dark';

function getInitialTheme(): Theme {
  const saved = localStorage.getItem('zeta-theme') as Theme | null;
  if (saved) return saved;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export default function App() {
  const [code, setCode] = useState(getCodeFromHash() || DEFAULT_CODE);
  const [output, setOutput] = useState('');
  const [errors, setErrors] = useState<ZetaError[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [awaitingInput, setAwaitingInput] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isBooting, setIsBooting] = useState(false);
  const [showExamples, setShowExamples] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const bootingRef = useRef(false);
  const examplesRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // An interactive run: Zeta programs are deterministic and side-effect-free
  // (no loops/conditionals/IO beyond tell/ask), so we simulate a prompt-and-wait
  // console by re-running with one more input line each time the program blocks
  // on `ask`. Each re-run extends stdout by exactly the next prompt.
  const sessionRef = useRef<{ inputs: string[]; transcript: string; prevClean: string }>({
    inputs: [],
    transcript: '',
    prevClean: '',
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('zeta-theme', theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme(t => t === 'light' ? 'dark' : 'light');
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      const decoded = getCodeFromHash();
      if (decoded) setCode(decoded);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (examplesRef.current && !examplesRef.current.contains(event.target as Node)) {
        setShowExamples(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleShare = useCallback(() => {
    setCodeHash(code);
    navigator.clipboard.writeText(window.location.href).then(() => {
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 1500);
    });
  }, [code]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === '?' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const target = e.target as HTMLElement;
        if (target.tagName !== 'TEXTAREA' && target.tagName !== 'INPUT' && !target.closest('.cm-editor')) {
          e.preventDefault();
          setShowShortcuts(s => !s);
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleShare();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'e') {
        e.preventDefault();
        setShowExamples(true);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        handleRun();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleShare]);

  // Run the program with the inputs gathered so far. If it blocks on an `ask`
  // (signalled by NoSuchElementException once stdin runs dry), pause and wait
  // for the next line; otherwise show the finished output.
  const runStep = useCallback(async () => {
    const session = sessionRef.current;
    setIsRunning(true);
    setAwaitingInput(false);

    const timeoutId = setTimeout(() => {
      setIsRunning(false);
      setOutput('Execution timed out after 10 seconds');
    }, 10000);

    try {
      const result: RunResult = await runZeta(code, session.inputs.join('\n'));
      clearTimeout(timeoutId);

      // CheerpJ routes the JVM's stderr (including uncaught-exception traces) to
      // console.log, so check both streams. A NoSuchElementException means the
      // program reached an `ask` after stdin ran dry — i.e. it wants more input.
      const combined = result.stdout + '\n' + result.stderr;
      const needsInput = /NoSuchElementException/.test(combined);

      // The program's real output is everything it printed before the JVM dumped
      // a stack trace; strip the trace and any trailing blank lines it added.
      const traceAt = result.stdout.search(/Exception in thread|java\.[\w.]*Exception/);
      const nowClean = (traceAt >= 0 ? result.stdout.slice(0, traceAt) : result.stdout).replace(/\s+$/, '');

      // Each re-run extends the prior output (deterministic program); append only
      // what's new since the last run, so the transcript reads like one session.
      if (nowClean.startsWith(session.prevClean)) {
        const delta = nowClean.slice(session.prevClean.length).replace(/^\n+/, '');
        if (delta) session.transcript += (session.transcript ? '\n' : '') + delta;
      } else {
        session.transcript = nowClean;
      }
      session.prevClean = nowClean;

      if (!needsInput) {
        const errorTail = ((traceAt >= 0 ? result.stdout.slice(traceAt) : '') + '\n' + result.stderr).trim();
        if (errorTail) {
          session.transcript += (session.transcript ? '\n' : '') + errorTail;
        } else if (result.exitCode !== 0 && !nowClean) {
          session.transcript = 'Program exited with code ' + result.exitCode;
        }
      }

      setOutput(session.transcript);
      setErrors(result.errors);
      setAwaitingInput(needsInput);
    } catch (e) {
      clearTimeout(timeoutId);
      setOutput('Runtime error: ' + (e instanceof Error ? e.message : String(e)));
      setAwaitingInput(false);
    } finally {
      setIsRunning(false);
    }
  }, [code]);

  const handleRun = useCallback(async () => {
    if (isRunning || bootingRef.current) return;

    if (!isCheerpJReady()) {
      setIsBooting(true);
      bootingRef.current = true;
      try {
        await initCheerpJ();
      } catch (e) {
        setOutput('Failed to initialize Zeta runtime: ' + (e instanceof Error ? e.message : String(e)));
        setIsBooting(false);
        bootingRef.current = false;
        return;
      }
      setIsBooting(false);
      bootingRef.current = false;
    }

    sessionRef.current = { inputs: [], transcript: '', prevClean: '' };
    setOutput('');
    setErrors([]);
    setInputValue('');
    await runStep();
  }, [isRunning, runStep]);

  // The user answered an `ask`: echo the line into the transcript and re-run
  // with it appended so the program advances to the next prompt (or finishes).
  const submitInput = useCallback(async () => {
    if (!awaitingInput || isRunning) return;
    const session = sessionRef.current;
    session.inputs = [...session.inputs, inputValue];
    // Echo the answer on its own line so the transcript reads like a console.
    session.transcript += (session.transcript ? '\n' : '') + inputValue;
    setOutput(session.transcript);
    setInputValue('');
    setAwaitingInput(false);
    await runStep();
  }, [awaitingInput, isRunning, inputValue, runStep]);

  const loadExample = useCallback((exampleCode: string) => {
    setCode(exampleCode);
    setOutput('');
    setErrors([]);
    setShowExamples(false);
  }, []);

  const clearOutput = useCallback(() => {
    setOutput('');
    setAwaitingInput(false);
  }, []);

  useEffect(() => {
    if (awaitingInput) inputRef.current?.focus();
  }, [awaitingInput]);

  const statusText = isBooting
    ? 'Booting Zeta runtime...'
    : isRunning
    ? 'Running...'
    : awaitingInput
    ? 'Awaiting input...'
    : errors.length > 0
    ? `${errors.length} error${errors.length > 1 ? 's' : ''}`
    : 'Ready';

  const statusClass = errors.length > 0 ? 'has-errors' : (isRunning || awaitingInput) ? 'is-running' : '';

  return (
    <div className="app">
      {isBooting && (
        <div className="boot-overlay">
          <div className="boot-content">
            <div className="boot-dots">
              <span /><span /><span />
            </div>
            <div className="boot-text">Booting Zeta runtime...</div>
          </div>
        </div>
      )}

      {showCopied && (
        <div className="toast">Copied to clipboard</div>
      )}

      <header className="toolbar">
        <div className="toolbar-left">
          <div className="logo-group">
            <span className="logo">ZETA</span>
            <span className="logo-sub">playground</span>
          </div>
        </div>
        <div className="toolbar-right">
          <button
            className="btn run-btn-desktop"
            onClick={handleRun}
            disabled={isRunning || isBooting}
          >
            {isRunning ? 'Running...' : 'Run'}
          </button>
          <div className="dropdown-container" ref={examplesRef}>
            <button
              className="btn"
              onClick={() => setShowExamples(!showExamples)}
              disabled={isRunning || isBooting}
            >
              [Examples]
            </button>
            {showExamples && (
              <div className="dropdown-menu">
                {EXAMPLES.map((ex) => (
                  <button
                    key={ex.name}
                    className="dropdown-item"
                    onClick={() => loadExample(ex.code)}
                  >
                    {ex.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            className="btn"
            onClick={() => setShowAbout(true)}
            disabled={isRunning || isBooting}
          >
            [About]
          </button>
          <button
            className="btn"
            onClick={handleShare}
            disabled={isRunning || isBooting}
          >
            Share
          </button>
          <button
            className="btn"
            onClick={toggleTheme}
            disabled={isRunning || isBooting}
            title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          >
            {theme === 'light' ? '[Dark]' : '[Light]'}
          </button>
          <button
            className="btn"
            onClick={() => setShowShortcuts(true)}
            disabled={isRunning || isBooting}
            title="Keyboard shortcuts"
          >
            ?
          </button>
          <a
            href="https://github.com/AliHamzaAzam/Zeta"
            target="_blank"
            rel="noopener noreferrer"
            className="btn link"
          >
            GitHub
          </a>
        </div>
      </header>

      <main className="main">
        <div className="editor-pane">
          <div className="pane-header">
            <span className="pane-label">Editor</span>
          </div>
          <div className="pane-content">
            <Editor
              value={code}
              onChange={setCode}
              errors={errors}
              onRun={handleRun}
              theme={theme}
            />
          </div>
        </div>
        <div className="output-pane">
          <div className="pane-header">
            <span className="pane-label">Output</span>
            {(output || awaitingInput) && (
              <button className="clear-btn" onClick={clearOutput}>
                Clear
              </button>
            )}
          </div>
          <pre className="output-content">{output}</pre>
          {awaitingInput && (
            <form
              className="stdin-row"
              onSubmit={(e) => {
                e.preventDefault();
                submitInput();
              }}
            >
              <span className="stdin-caret">&rsaquo;</span>
              <input
                ref={inputRef}
                className="stdin-line"
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="type a value, press Enter"
                spellCheck={false}
                autoComplete="off"
                aria-label="Program input"
              />
            </form>
          )}
        </div>
      </main>

      <footer className="statusbar">
        <div className="statusbar-left">
          <button
            className="run-btn"
            onClick={handleRun}
            disabled={isRunning || isBooting}
          >
            {isRunning ? 'Running...' : 'Run'}
          </button>
          <span className={`status-text ${statusClass}`}>
            {statusText}
          </span>
        </div>
        <div className="statusbar-right">
          <a href="https://alihamzaazam.com" target="_blank" rel="noopener noreferrer" className="footer-link">[Ali Hamza]</a>
          <a href="https://github.com/AliHamzaAzam/Zeta" target="_blank" rel="noopener noreferrer" className="footer-link">[Zeta]</a>
          <a href="https://github.com/AliHamzaAzam/zeta-playground" target="_blank" rel="noopener noreferrer" className="footer-link">[Playground]</a>
        </div>
      </footer>

      <AboutPanel isOpen={showAbout} onClose={() => setShowAbout(false)} />
      <ShortcutsOverlay isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />
    </div>
  );
}
